/*
 * Varianten-Kachel-Picker — die Kacheln aus dem Combined-Products-Design,
 * hier aber als Radio-Buttons eines ganz normalen Varianten-Produkts.
 *
 * Den Variantenwechsel selbst macht <variant-radios> aus assets/global.js:
 * Preis, Galerie, URL und Kaufen-Button haengen am change-Event der Radios.
 * Dieses Element kuemmert sich nur um die Optik drumherum:
 *
 *   1. gewaehlte Kachel markieren (data-selected) und den Wert neben der
 *      Optionsbezeichnung mitschreiben ("Sorte: Fruit Mix")
 *   2. nicht lieferbare Werte markieren — bei der zweiten und dritten Option
 *      setzt global.js dafuer die Klasse .disabled auf das Radio
 *   3. Kacheln, die nicht in die erste Reihe passen, hinter "+N anzeigen"
 *      einklappen; die gewaehlte Kachel bleibt dabei immer sichtbar
 *
 * Gerendert von snippets/variant-tiles-picker.liquid.
 */

if (!customElements.get('variant-tiles')) {
  class VariantTiles extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.handleChange.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.expanded = false;
      this.resizeRaf = null;
    }

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-variant-option]'));
      this.selectedLabel = this.querySelector('[data-selected-label]');
      this.toggleButton = this.querySelector('[data-tiles-toggle]');
      this.toggleText = this.querySelector('[data-tiles-toggle-text]');
      this.tilesMobile = parseInt(this.dataset.tilesMobile, 10) || 2;
      this.tilesDesktop = parseInt(this.dataset.tilesDesktop, 10) || 4;
      this.showMoreText = this.dataset.showMore || '';
      this.showLessText = this.dataset.showLess || '';

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      window.addEventListener('resize', this.onResize);

      this.syncItems();
      this.applyCollapse();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('resize', this.onResize);
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
    }

    handleChange() {
      this.syncItems();
      this.applyCollapse();
    }

    handleClick(event) {
      if (!event.target.closest('[data-tiles-toggle]')) return;
      event.preventDefault();
      this.expanded = !this.expanded;
      this.applyCollapse();
    }

    handleResize() {
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => this.applyCollapse());
    }

    /*
     * data-selected und data-sold-out stehen am <label>, denn daran haengt die
     * ganze Optik in combined-products.css. Beides wird vom Radio abgeleitet:
     * ausverkauft ist ein Wert, den Liquid schon so ausgeliefert hat
     * (data-sold-out), oder einer, den global.js beim Wechsel der ersten
     * Option nachtraeglich als .disabled markiert hat.
     */
    syncItems() {
      this.items.forEach((item) => {
        const input = item.control;
        if (!input) return;

        item.toggleAttribute('data-selected', input.checked);
        item.toggleAttribute('data-sold-out', input.hasAttribute('data-sold-out') || input.classList.contains('disabled'));

        if (input.checked && this.selectedLabel) this.selectedLabel.textContent = input.value;
      });
    }

    // Kacheln pro Reihe im aktuellen Breakpoint. Die letzte Kachel einer
    // eingeklappten Reihe ist der Umschalter, also bleiben `tiles - 1` Werte stehen.
    currentTiles() {
      return window.matchMedia('(min-width: 750px)').matches ? this.tilesDesktop : this.tilesMobile;
    }

    applyCollapse() {
      if (!this.toggleButton) return;

      const tiles = this.currentTiles();
      const total = this.items.length;

      // Passt alles in eine Reihe → kein Umschalter, alle Kacheln sichtbar.
      if (total <= tiles) {
        this.toggleButton.removeAttribute('data-show');
        this.removeAttribute('data-collapsed');
        this.items.forEach((item) => item.removeAttribute('data-overflow'));
        return;
      }

      const visibleValues = tiles - 1;
      const hidden = total - visibleValues;
      this.toggleButton.setAttribute('data-show', '');

      if (this.expanded) {
        this.removeAttribute('data-collapsed');
        this.items.forEach((item) => item.removeAttribute('data-overflow'));
        if (this.toggleText) this.toggleText.textContent = this.showLessText;
        return;
      }

      this.setAttribute('data-collapsed', '');
      this.items.forEach((item, index) => {
        item.toggleAttribute('data-overflow', index >= visibleValues);
      });

      // Die gewaehlte Kachel bleibt sichtbar, auch wenn sie im
      // eingeklappten Bereich liegt — dafuer weicht die letzte sichtbare.
      const selectedIndex = this.items.findIndex((item) => item.hasAttribute('data-selected'));
      if (selectedIndex >= visibleValues) {
        this.items[selectedIndex].removeAttribute('data-overflow');
        this.items[visibleValues - 1].setAttribute('data-overflow', '');
      }

      if (this.toggleText) this.toggleText.textContent = `+${hidden} ${this.showMoreText}`;
    }
  }

  customElements.define('variant-tiles', VariantTiles);
}
